import { useFreeTrialEligibilityRulesQuery } from './FreeTrialEligibilityRulesQuery.generated';
import { useAddFreeTrialCreditMutation } from 'app/src/components/UpgradePrompts/AddFreeTrialCreditMutation.generated';
import { useFreeTrialProtectedQuery } from './FreeTrialProtectedQuery.generated';
import { getFreeTrialProperties, TrialSource } from '@trello/organizations';
import { needsCC } from '@trello/paid-account';

import { useFeatureFlag } from '@trello/feature-flag-client';
import { navigate } from 'app/scripts/controller/navigate';
import experiment from 'app/gamma/src/util/experiments';
export interface FreeTrialRules {
  isAdmin: boolean;
  hasCredit: boolean;
  isTrialActive?: boolean;
  isExpired?: boolean;
  isEligible?: boolean;
  loading: boolean;
  daysLeft: number;
  trialSource?: TrialSource;
  startDate?: Date;
  days?: number;
  startFreeTrial: () => Promise<void>;
}

export const useFreeTrialEligibilityRules = (
  orgId?: string,
  options: { skip: boolean } = { skip: false },
): FreeTrialRules => {
  const {
    data: dataProtected,
    loading: loadingProtected,
  } = useFreeTrialProtectedQuery(
    orgId && !options.skip
      ? {
          variables: {
            orgId: orgId,
          },
        }
      : { skip: true },
  );

  const meId = dataProtected?.member?.id;

  const isMember = dataProtected?.organization?.memberships.find(
    (member) => member.idMember === meId,
  );

  const { data, refetch, loading } = useFreeTrialEligibilityRulesQuery(
    orgId && isMember && !options.skip
      ? {
          variables: {
            orgId: orgId,
          },
        }
      : { skip: true },
  );

  const [addFreeTrialCredit] = useAddFreeTrialCreditMutation();
  const bcOnboardingFeatureFlag = useFeatureFlag(
    'btg.bc-team-onboarding',
    false,
  );

  const orgName = data?.organization?.name;
  const credits = data?.organization?.credits || [];
  const products = data?.organization?.products || [];
  const isDisabled = needsCC(data?.organization?.paidAccount?.standing);
  const trialProperties = getFreeTrialProperties(credits, products);
  const hasCredit = !!trialProperties?.credit;

  const isAdmin = Boolean(
    meId &&
      !!data?.organization?.memberships.find(
        (member) => member.idMember === meId && member.memberType === 'admin',
      ),
  );

  const startFreeTrial = async (onFreeTrial?: () => void) => {
    if (!orgId || loading || hasCredit) {
      return;
    }

    await addFreeTrialCredit({
      variables: {
        orgId,
      },
    });

    if (orgName && bcOnboardingFeatureFlag) {
      const test = experiment.get('bcTeamOnboarding', orgId);
      test.track();
      if (test.get('variation') === 'enabled') {
        navigate(`/${orgName}/getting-started`, {
          trigger: true,
        });

        return;
      }
    }

    await refetch();

    onFreeTrial?.();
  };

  return {
    startFreeTrial,
    isAdmin,
    hasCredit,
    isTrialActive: trialProperties?.isActive,
    isExpired: trialProperties?.isExpired,
    isEligible: isAdmin && !trialProperties?.credit && !isDisabled,
    loading: loading || loadingProtected,
    daysLeft: trialProperties?.daysLeft || 0,
    trialSource: trialProperties?.trialSource,
    startDate: trialProperties?.startDate,
    days: trialProperties?.days,
  };
};
